import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Membership } from './types';

interface ExpirationAlertProps {
  membership: Membership;
  onRenew?: () => void;
}

export interface ExpirationStatus {
  isExpiring: boolean;
  daysLeft: number;
  urgency: 'warning' | 'critical' | 'none';
}

export const getExpirationStatus = (membership: Membership | null): ExpirationStatus => {
  if (!membership) return { isExpiring: false, daysLeft: 0, urgency: 'none' };
  
  const endDate = new Date(membership.end_date);
  const today = new Date();
  const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  
  // Thresholds based on billing period
  let warningThreshold: number;
  let criticalThreshold: number;
  
  switch (membership.billing_period) {
    case 'mensual':
      warningThreshold = 7;
      criticalThreshold = 3;
      break;
    case 'anual':
      warningThreshold = 30;
      criticalThreshold = 14;
      break;
    default:
      warningThreshold = 7;
      criticalThreshold = 3;
  }
  
  if (daysLeft <= criticalThreshold) {
    return { isExpiring: true, daysLeft, urgency: 'critical' };
  } else if (daysLeft <= warningThreshold) {
    return { isExpiring: true, daysLeft, urgency: 'warning' };
  }
  
  return { isExpiring: false, daysLeft, urgency: 'none' };
};

export const ExpirationAlert: React.FC<ExpirationAlertProps> = ({ membership, onRenew }) => {
  const { isExpiring, daysLeft, urgency } = getExpirationStatus(membership);
  
  if (!isExpiring) return null;
  
  const isCritical = urgency === 'critical';
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        className={`rounded-2xl p-4 border backdrop-blur-xl mb-4 ${
          isCritical
            ? 'bg-red-500/20 border-red-500/30'
            : 'bg-amber-500/20 border-amber-500/30'
        }`}
      >
        <div className="flex items-start gap-3">
          <motion.div
            animate={{ 
              scale: isCritical ? [1, 1.2, 1] : 1,
              rotate: isCritical ? [0, -10, 10, 0] : 0
            }}
            transition={{ 
              duration: 0.5, 
              repeat: isCritical ? Infinity : 0,
              repeatDelay: 2
            }}
            className={`p-2 rounded-full ${
              isCritical ? 'bg-red-500/20' : 'bg-amber-500/20'
            }`}
          >
            <AlertTriangle className={`w-5 h-5 ${
              isCritical ? 'text-red-400' : 'text-amber-400'
            }`} />
          </motion.div>
          
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold ${
              isCritical ? 'text-red-300' : 'text-amber-300'
            }`}>
              {isCritical ? '¡Tu membresía vence pronto!' : 'Membresía próxima a vencer'}
            </h4>
            <p className="text-white/70 text-sm mt-1">
              <Clock className="w-3.5 h-3.5 inline-block mr-1" />
              Te quedan <span className={`font-bold ${
                isCritical ? 'text-red-300' : 'text-amber-300'
              }`}>{daysLeft} {daysLeft === 1 ? 'día' : 'días'}</span> para renovar
            </p>
          </div>
          
          {onRenew && (
            <Button
              onClick={onRenew}
              size="sm"
              className={`shrink-0 ${
                isCritical
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
              }`}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Renovar Ahora
            </Button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExpirationAlert;
